import React, { useState, useEffect } from 'react';

import {
  makeStyles,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  RadioGroup,
  Radio,
  Checkbox,
} from 'design-system';
import { TextInput } from 'react-scale/components/design-system';
// eslint-disable-next-line restrict-imports/restrict-imports
import CloseIcon from '@material-ui/icons/Close';

export const GRAMMAR_SPELLING_LLM_PROMPT = `Here are two versions of a piece of text, one is an unprocessed copy and the second is a copy of the same text that has been edited to fix spelling and grammar errors only if they exist. If there are no errors the text remains the same.

Example Input: Inher33ent Risk:
Example Output: Inherent Risk:

Input: {{value}}
Output:
`;

interface TextBasedSpeedAuditEditModalProps {
  isOpen: boolean;
  fields: string[];
  onEdit: (fields: string[], prompt?: string) => void;
  onRequestClose: () => void;
}

const TextBasedSpeedAuditEditModal: React.FC<TextBasedSpeedAuditEditModalProps> = ({
  isOpen,
  fields,
  onEdit,
  onRequestClose,
}) => {
  const classes = useStyles();
  const [prompt, setPrompt] = useState('');
  // will have at most one item, but matches type of editor endpoint
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [mode, setMode] = useState('0');

  useEffect(() => {
    if (mode === 'spelling-grammar') {
      setPrompt(GRAMMAR_SPELLING_LLM_PROMPT);
    } else {
      setPrompt('');
    }
  }, [mode]);

  return (
    <Dialog maxWidth="md" open={isOpen} onClose={onRequestClose} fullWidth>
      <DialogTitle>
        <div className={classes.close}>
          <IconButton aria-label="close" size="small" onClick={onRequestClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <Typography className={classes.header}>LLM Task Editor</Typography>
        <Typography className={classes.subtitle}>Use an LLM to edit fields</Typography>
      </DialogTitle>

      <DialogContent>
        <div className={classes.container}>
          <span>
            This will query an LLM to provide suggested edits to a given field. You can either input
            a custom prompt to query against with an LLM or you can fix spelling & grammar issues.
            Note that this just sends the prompt to an LLM and displays its response, so you may get
            some responses that are don't make sense or have unexpected formatting.
          </span>
          Select one field which you will be editing.
          <div className={classes.flagGroup}>
            {fields.map(field => (
              <Checkbox
                key={field}
                className={classes.checkbox}
                checked={selectedFields.includes(field)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSelectedFields(current => {
                    return event.target.checked
                      ? [...current, event.target.name]
                      : current.filter(field => field !== event.target.name);
                  });
                }}
                name={field}
                label={field}
              />
            ))}
          </div>
          Choose one of the following (Edit with prompt or Fix Spelling & Grammar){' '}
          <div className={classes.flagGroup}>
            <RadioGroup value={mode} onChange={(value: string) => setMode(value)}>
              <Radio
                className={classes.checkbox}
                key="spelling-grammar"
                value="spelling-grammar"
                label="Spelling & Grammar"
              />
              <Radio
                className={classes.checkbox}
                key="custom"
                value="custom"
                label="Custom Prompt"
              />
            </RadioGroup>
          </div>
          <div className={classes.container}>
            <span>
              Prompts can reference any number of fields, but edits will just be suggested for the
              selected field above. To reference a field in a prompt, write it as {'{{Field Name}}'}
            </span>
            <TextInput
              fullWidth
              multiline
              size="large"
              label={'Prompt'}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder={
                'Here are two versions of a piece of text, one is an unprocessed copy and the second is a copy of the same text that has been edited to fix spelling and grammar errors only if they exist. If there are no errors the text remains the same:\nInput: {{value}}\nOutput:'
              }
            />
            <Button
              className={classes.completeButton}
              disabled={selectedFields.length === 0}
              onClick={() => {
                onEdit(selectedFields, prompt);
              }}
            >
              Edit with Prompt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    color: 'gray',
  },
  close: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: () => 'gray',
  },
  flagGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  checkbox: {
    '&.Mui-checked': {
      color: 'rgb(25, 118, 210, 1)',
    },
  },
  completeButton: {
    padding: '6px 16px !important',
    color: 'white',
    background: 'rgb(25, 118, 210, 1)',
    '&:hover': {
      background: 'rgb(25, 118, 210, 0.75)',
    },
  },
}));

export default TextBasedSpeedAuditEditModal;
